import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import {
  IHealthcareFacility,
  IHealthcareFacilityPayload,
  IHealthcareFacilityDefaultTask,
  IHealthcareFacilityDefaultTaskInput,
  IClinicBranding,
} from '../../entities/healthcareFacility/HealthcareFacility.ts';
import { S3UploadPolicy } from '../Documents/DocumentApi.ts';
import { ITherapeuticIndication } from '../../entities/TherapeuticIndication/TherapeuticIndication.ts';
import { IQuestionnaires } from '../../entities/Questionnaires/Questionnaires';
import { IPatient } from '../../entities/Patients/Patient.ts';

export class HealthcareFacilitiesApi extends BaseApi {
  private static baseApiUrl: string = `${BaseApi.apiUrl}/HealthcareFacilities`;

  public static apiUrls: {
    getHealthcareFacilities: string;
    postHealthcareFacility: string;
    getHealthcareFacilityNew: string;
    getHealthcareFacilitiesById: string;
    deleteHealthcareFacilitiesById: string;
    putHealthcareFacilitiesById: string;
    getHealthcareFacilitiesForHcp: string;
    getTherapeuticIndicationsForHcp: string;
    getQuestionnairesForHcp: string;
  } = {
    getHealthcareFacilities: `${HealthcareFacilitiesApi.baseApiUrl}`,
    postHealthcareFacility: `${HealthcareFacilitiesApi.baseApiUrl}`,
    getHealthcareFacilityNew: `${HealthcareFacilitiesApi.baseApiUrl}/New`,
    getHealthcareFacilitiesById: `${HealthcareFacilitiesApi.baseApiUrl}/:id`,
    deleteHealthcareFacilitiesById: `${HealthcareFacilitiesApi.baseApiUrl}/:id`,
    putHealthcareFacilitiesById: `${HealthcareFacilitiesApi.baseApiUrl}/:id`,
    getHealthcareFacilitiesForHcp: `${HealthcareFacilitiesApi.baseApiUrl}/ForHcp/:id`,
    getTherapeuticIndicationsForHcp: `${HealthcareFacilitiesApi.baseApiUrl}/TherapeuticIndications/ForHcp/:id`,
    getQuestionnairesForHcp: `${HealthcareFacilitiesApi.baseApiUrl}/Questionnaires/ForHcp/:id`,
  };

  public static async getHealthcareFacilities(): Promise<
    IHealthcareFacility[] | undefined
  > {
    return this.sendApiRequest<[IHealthcareFacility]>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getHealthcareFacilities,
    });
  }

  public static async postHealthcareFacility(
    data: IHealthcareFacilityPayload,
  ): Promise<IHealthcareFacility> {
    return this.sendApiRequest<IHealthcareFacility>({
      method: HttpMethodEnum.POST,
      path: this.apiUrls.postHealthcareFacility,
      data: data,
    });
  }

  public static async getHealthcareFacilitiesById(
    id: string,
  ): Promise<IHealthcareFacility | undefined> {
    return this.sendApiRequest<IHealthcareFacility>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getHealthcareFacilitiesById,
      data: undefined,
      id: id,
    });
  }

  public static async getHealthcareFacilityNew(): Promise<
    IHealthcareFacility | undefined
  > {
    return this.sendApiRequest<IHealthcareFacility>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getHealthcareFacilityNew,
    });
  }

  public static async deleteHealthcareFacilitiesById(
    id: string,
  ): Promise<void> {
    return this.sendApiRequest<void>({
      method: HttpMethodEnum.DELETE,
      path: this.apiUrls.deleteHealthcareFacilitiesById,
      data: undefined,
      id: id,
    });
  }

  public static async putHealthcareFacilitiesById(
    data: IHealthcareFacilityPayload,
    id: string,
  ): Promise<IHealthcareFacility | undefined> {
    return this.sendApiRequest<IHealthcareFacility>({
      method: HttpMethodEnum.PUT,
      path: this.apiUrls.putHealthcareFacilitiesById,
      data: data,
      id: id,
    });
  }

  static async getHealthcareFacilitiesForHcp(
    id: number,
  ): Promise<Array<{ id: number; name: string; isAnonymizedMode: boolean }>> {
    const response = await this.sendApiRequest<
      Array<{ id: number; name: string; isAnonymizedMode: boolean }>
    >({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getHealthcareFacilitiesForHcp,
      data: undefined,
      id: id.toString(),
    });
    return response;
  }

  static async getTherapeuticIndicationsForHcp(
    id: number,
  ): Promise<ITherapeuticIndication[]> {
    const response = await this.sendApiRequest<ITherapeuticIndication[]>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getTherapeuticIndicationsForHcp,
      data: undefined,
      id: id.toString(),
    });
    return response;
  }

  public static getQuestionnairesForHcp(
    hcpId: number,
  ): Promise<IQuestionnaires[]> {
    return this.sendApiRequest<IQuestionnaires[]>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.getQuestionnairesForHcp,
      data: undefined,
      id: hcpId.toString(),
    });
  }

  public static getQuestionnaireIdsByFacilityId(
    facilityId: number,
  ): Promise<number[]> {
    return this.sendApiRequest<number[]>({
      method: HttpMethodEnum.GET,
      path: `${this.baseApiUrl}/${facilityId}/QuestionnaireIds`,
      data: undefined,
    });
  }

  public static getDefaultTasksByFacilityId(
    facilityId: number,
  ): Promise<IHealthcareFacilityDefaultTask[]> {
    return this.sendApiRequest<IHealthcareFacilityDefaultTask[]>({
      method: HttpMethodEnum.GET,
      path: `${this.baseApiUrl}/${facilityId}/DefaultTasks`,
      data: undefined,
    });
  }

  public static postDefaultTasksForFacility(
    facilityId: number,
    tasks: IHealthcareFacilityDefaultTaskInput[],
  ): Promise<void> {
    return this.sendApiRequest<void>({
      method: HttpMethodEnum.POST,
      path: `${this.baseApiUrl}/${facilityId}/DefaultTasks`,
      data: tasks,
    });
  }

  public static async getClinicPatients(
    healthcareFacilityID: number,
  ): Promise<IPatient[]> {
    return this.sendApiRequest<IPatient[]>({
      method: HttpMethodEnum.GET,
      path: `${this.baseApiUrl}/${healthcareFacilityID}/Patients`,
    });
  }

  public static async getLogoUploadUrl(
    facilityId: number,
    filename: string,
  ): Promise<S3UploadPolicy> {
    return this.sendApiRequest<S3UploadPolicy>({
      method: HttpMethodEnum.GET,
      path: `${this.baseApiUrl}/${facilityId}/Logo/UploadUrl/${filename}`,
    });
  }

  public static async getMyClinicBranding(): Promise<
    IClinicBranding | undefined
  > {
    return this.sendApiRequest<IClinicBranding>({
      method: HttpMethodEnum.GET,
      path: `${this.baseApiUrl}/MyBranding`,
    });
  }

  public static async deleteLogo(facilityId: number): Promise<void> {
    return this.sendApiRequest<void>({
      method: HttpMethodEnum.DELETE,
      path: `${this.baseApiUrl}/${facilityId}/Logo`,
    });
  }
}
